import * as THREE from "three";
import type { GraspCandidate } from "../../types/grasps";

const GRASP_ROLL_OFFSET_RAD = Math.PI / 2;

// ---------------------------------------------------------------------------
// Grasp candidate selection
// ---------------------------------------------------------------------------

/**
 * Given a set of object-local grasps and the body's current world quaternion
 * (in Y-up scene frame), return the best grasp expressed in world Z-up frame.
 *
 * "Best" = highest score in the grasps.json (which already encodes top-down
 * preference from extract_grasps.py). We simply pick the top candidate since
 * the file is pre-sorted by score descending and the scores encode the
 * top-down quality we care most about for pick-and-place.
 *
 * Frame chain:
 *   object-local Z-up  →  world Z-up  →  (Y-up scene, used only for position)
 *
 * The returned position and orientation are in world Z-up (MuJoCo chain frame)
 * ready to be passed directly to solvePositionIk as IkTarget.
 */
export type RankedGraspWorldZ = {
  positionZ: THREE.Vector3;
  orientationZ: THREE.Quaternion;
  sourceIndex: number;
  combinedScore: number;
  topDownScore: number;
  distanceScore: number;
};

export function rankGraspsWorldZUp(args: {
  grasps: GraspCandidate[];
  /** World quaternion of the object body, in Y-up scene frame. */
  bodyWorldQuatScene: THREE.Quaternion;
  /** World position of the object body centroid, in Y-up scene frame. */
  bodyWorldPosScene: THREE.Vector3;
  /** Conversion quaternion: Y-up scene → Z-up MuJoCo chain frame. */
  yToZ: THREE.Quaternion;
  /**
   * Optional current end-effector world position in Y-up scene frame.
   * When provided, prefer grasps that are closer to current EE pose.
   */
  eeWorldPosScene?: THREE.Vector3 | null;
}): RankedGraspWorldZ[] {
  const { grasps, bodyWorldQuatScene, bodyWorldPosScene, yToZ, eeWorldPosScene = null } = args;
  if (!grasps.length) return [];

  // Grasp position in object-local Z-up frame -> object-local Y-up -> world Y-up.
  // IMPORTANT: bodyWorldQuatScene is taken from the runtime poseGroup, which already
  // includes the scene's Z-up -> Y-up conversion. So grasp local coordinates from
  // grasps.json (object-local Z-up) should be transformed directly by bodyWorldQuatScene
  // without an extra basis rotation first.
  const worldDownScene = new THREE.Vector3(0, -1, 0);

  const toWorldFrames = (grasp: GraspCandidate) => {
    // Grasp local position in Z-up object frame.
    const localPosZUp = new THREE.Vector3(...grasp.position);
    // Rotate by body's world quaternion (scene Y-up frame) and offset.
    const worldPosScene = localPosZUp
      .clone()
      .applyQuaternion(bodyWorldQuatScene)
      .add(bodyWorldPosScene);
    // Convert to Z-up chain frame.
    const worldPosZ = worldPosScene.clone().applyQuaternion(yToZ);

    // Grasp orientation in object-local Z-up frame [w,x,y,z].
    const [qw, qx, qy, qz] = grasp.orientation;
    const rawQuat = new THREE.Quaternion(qx, qy, qz, qw);
    const rollOffset = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 0, 1),
      GRASP_ROLL_OFFSET_RAD
    );
    const graspLocalQuatZUp = rawQuat.clone().multiply(rollOffset);
    // Apply body world quaternion.
    const graspWorldQuatScene = bodyWorldQuatScene.clone().multiply(graspLocalQuatZUp);
    // Convert world orientation from scene Y-up basis back to chain Z-up basis.
    // Since the local grasp frame is already Z-up, we only need world basis conversion.
    const graspWorldQuatZ = yToZ.clone().multiply(graspWorldQuatScene);

    return { worldPosScene, worldPosZ, graspWorldQuatScene, graspWorldQuatZ };
  };

  const ranked: RankedGraspWorldZ[] = [];
  for (let sourceIndex = 0; sourceIndex < grasps.length; sourceIndex += 1) {
    const grasp = grasps[sourceIndex];
    const { worldPosScene, worldPosZ, graspWorldQuatScene, graspWorldQuatZ } = toWorldFrames(grasp);

    // Favor top-down approach in world frame (same convention as grasp visualizer).
    const approachWorldScene = new THREE.Vector3(0, 0, 1)
      .applyQuaternion(graspWorldQuatScene)
      .normalize();
    const topDownScore = Math.max(0, Math.min(1, (approachWorldScene.dot(worldDownScene) + 1) / 2));

    // Favor grasps that require less translation from current EE pose.
    const distanceScore =
      eeWorldPosScene instanceof THREE.Vector3
        ? 1 / (1 + worldPosScene.distanceTo(eeWorldPosScene))
        : 0.5;

    // Primary objective: top-down quality. Secondary: proximity/efficiency.
    const combinedScore = topDownScore * 0.8 + distanceScore * 0.2;
    ranked.push({
      positionZ: worldPosZ,
      orientationZ: graspWorldQuatZ,
      sourceIndex,
      combinedScore,
      topDownScore,
      distanceScore,
    });
  }

  ranked.sort((a, b) => b.combinedScore - a.combinedScore);
  return ranked;
}
